import Cookie from 'js-cookie';

import {
    updateSponsorApi,
    updateSponsorCnameApi,
    sponsorCnameMigrateApi,
    updateSponsorSettingsApi,
    getSponsor,
    updateThirdPartyPlatformInfo
} from 'api/sponsor';
import generateUpdatePayload from 'utils/generateUpdatePayload';
import { replaceAvailablePlanItems } from 'store/module/availablePlanItems';
import { setFailed } from 'store/module/flag';
import { cookieKeys } from 'config/cookie';
import { isNull, merge } from 'lodash';
import { setThirdPartyDataInitialized } from 'store/module/flags';

const INIT_SPONSOR = 'sponsor/INIT_SPONSOR';
const UPDATE_SPONSOR = 'sponsor/UPDATE_SPONSOR';
export const CHANGE_SPONSOR = 'sponsor/CHANGE_SPONSOR';
const defaultState = {
    settings: {},
    thirdParty: {},
    event: { settings: {} },
    channel: { settings: {} }
};

export default function reducer(state = defaultState, { type, payload }) {
    switch (type) {
        case UPDATE_SPONSOR:
            return {
                ...state,
                ...payload,
                settings: { ...state.settings, ...payload.settings },
                thirdParty: merge(state?.thirdParty || {}, payload?.thirdParty || {})
            };
        case CHANGE_SPONSOR:
        case INIT_SPONSOR:
            return { ...payload };
        default:
            return state;
    }
}

export function initSponsor(data) {
    return {
        type: INIT_SPONSOR,
        payload: data
    };
}

export function updateSponsorDataWithNoFetch(data) {
    return {
        type: UPDATE_SPONSOR,
        payload: data
    };
}

export function changeSponsor(sponsorId) {
    return async (dispatch, getState) => {
        const { sponsor: { id } = {} } = getState();
        if (sponsorId === id) {
            return;
        }
        try {
            const sponsorData = await getSponsor(sponsorId);
            // getSponsor 在 403 回錯誤描述物件、其他 4xx 回 null —— 兩者都沒有 sponsor
            // 可切換，不能往下 dispatch（否則會把 undefined 寫進 store）
            if (!sponsorData?.sponsor) {
                dispatch(setFailed('ChangeSponsor', 'message_connection_error'));
                return;
            }
            dispatch(setThirdPartyDataInitialized(false));
            dispatch(replaceAvailablePlanItems(sponsorData.availablePlanItems));
            dispatch({
                type: CHANGE_SPONSOR,
                payload: sponsorData.sponsor
            });
            Cookie.set(cookieKeys.LATEST_SELECTED_SPONSOR_ID, sponsorId, {
                expires: 7
            });
            return sponsorData.sponsor;
        } catch (error) {
            console.error(error);
            dispatch(setFailed('ChangeSponsor', 'message_connection_error'));
        }
    };
}

export function updateSponsorData(data) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id, settings: settingOriginData, thirdParty: thirdPartyOriginData = {} } = {}
        } = getState();
        const { settings, event, channel, thirdParty, ...sponsorInfoData } = data;
        let promises = [];
        if (Object.keys(sponsorInfoData).length !== 0) {
            promises.push(updateSponsorApi(id, sponsorInfoData));
        }

        if (settings) {
            const originSameSettings = getSameObjectKey(settingOriginData, settings);
            const removeEmptySettings = removeEmptyObject(settings);
            const addRemoveSettings = generateUpdatePayload(
                originSameSettings,
                removeEmptySettings
            );
            // 如果全部為 undefined 代表沒有更新。 PS. 有要清除的出現 deleted 陣列
            if (Object.values(addRemoveSettings).some(v => v !== undefined)) {
                promises.push(updateSponsorSettingsApi(id, addRemoveSettings));
            }
        }

        if (thirdParty) {
            Object.entries(thirdParty).forEach(([platform, data]) => {
                if (thirdPartyOriginData[platform]) {
                    const originPlatform = thirdPartyOriginData[platform];
                    const diffPayload = Object.entries(data).reduce((acc, [key, value]) => {
                        if (
                            originPlatform[key] === value ||
                            (isNull(originPlatform[key]) && isNull(value))
                        )
                            return acc;
                        if (
                            value === undefined ||
                            value === null ||
                            value === '' ||
                            (Array.isArray(value) && value.length === 0) ||
                            (typeof value === 'object' && Object.keys(value).length === 0)
                        ) {
                            // Third Party 沒有 deleted 的概念，需直接轉成空字串
                            return { ...acc, [key]: '' };
                        }
                        return { ...acc, [key]: value };
                    }, {});

                    if (Object.keys(diffPayload).length !== 0) {
                        promises.push(
                            updateThirdPartyPlatformInfo({
                                sponsorId: id,
                                platform,
                                settings: diffPayload
                            })
                        );
                    }
                } else {
                    promises.push(
                        updateThirdPartyPlatformInfo({ sponsorId: id, platform, settings: data })
                    );
                }
            });
        }

        await Promise.all(promises);

        dispatch({
            type: UPDATE_SPONSOR,
            payload: data
        });
    };
}

/**
 * 設定主辦單位自訂網域 (CNAME)
 * 改用 promotion-tool 的 PATCH /services/sponsors/{uuid}/cname API
 * ClickUp: https://app.clickup.com/t/86ex8g6re
 */
export function updateSponsorCname(customDomain) {
    return async (dispatch, getState) => {
        const {
            sponsor: { uuid }
        } = getState();
        await updateSponsorCnameApi(uuid, customDomain);
        dispatch(updateSponsorDataWithNoFetch({ customDomain }));
    };
}

/**
 * 修正主辦單位自訂網域 CNAME 指向
 * ClickUp: https://app.clickup.com/t/86exke5ef
 *
 * 後端沿用已存的 sponsors.custom_domain，不需傳 domain；
 * customDomain 沒變動，故不 dispatch updateSponsorDataWithNoFetch。
 */
export function migrateSponsorCname() {
    return async (_, getState) => {
        const {
            sponsor: { uuid }
        } = getState();
        await sponsorCnameMigrateApi(uuid);
    };
}

export function getSameObjectKey(origin, target) {
    return Object.keys(target).reduce((object, k) => {
        object[k] = origin[k];
        return object;
    }, {});
}

export function removeEmptyObject(obj) {
    return Object.keys(obj).reduce((object, k) => {
        if (obj[k] !== '') {
            object[k] = obj[k];
        }
        return object;
    }, {});
}
